import { gql, useLazyQuery, useMutation } from '@apollo/client';

const DEBT_HAND_OVER = gql`
  mutation DebtHandOver($stripeCustomerId: String!, $invoiceIds: [String!], $debtor: DebtorInput, $note: String) {
    debtHandOver(stripeCustomerId: $stripeCustomerId, invoiceIds: $invoiceIds, debtor: $debtor, note: $note) {
      stripeCustomerId
      status
      handedOver
      alreadyThere
      failed
      debtistReference
      error
    }
  }
`;

const DEBT_HAND_OVER_MANY = gql`
  mutation DebtHandOverMany($stripeCustomerIds: [String!]!) {
    debtHandOverMany(stripeCustomerIds: $stripeCustomerIds)
  }
`;

const DEBT_DECIDE = gql`
  mutation DebtDecide(
    $stripeCustomerId: String!
    $decision: String!
    $note: String
    $holdUntil: DateTime
    $contactEmail: String
  ) {
    debtDecide(
      stripeCustomerId: $stripeCustomerId
      decision: $decision
      note: $note
      holdUntil: $holdUntil
      contactEmail: $contactEmail
    )
  }
`;

export interface DebtorInput {
  businessName?: string | null;
  contactName?: string | null;
  email?: string | null;
  vatId?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  town?: string | null;
  country?: string | null;
}

export interface DebtHandoverResult {
  stripeCustomerId: string;
  status: string;
  handedOver: number;
  alreadyThere: number;
  failed: number;
  debtistReference: string | null;
  error: string | null;
}

export type DebtDecision = 'HOLD' | 'CLARIFY' | 'WRITE_OFF' | 'REOPEN';

export function useDebtHandOverMutation() {
  return useMutation<
    { debtHandOver: DebtHandoverResult },
    { stripeCustomerId: string; invoiceIds?: string[]; debtor?: DebtorInput; note?: string }
  >(DEBT_HAND_OVER);
}

export function useDebtHandOverManyMutation() {
  return useMutation<{ debtHandOverMany: number }, { stripeCustomerIds: string[] }>(DEBT_HAND_OVER_MANY);
}

export function useDebtDecideMutation() {
  return useMutation<
    { debtDecide: string },
    {
      stripeCustomerId: string;
      decision: DebtDecision;
      note?: string | null;
      holdUntil?: string | null;
      contactEmail?: string | null;
    }
  >(DEBT_DECIDE);
}

// ── Phase 3: Rückkanal nach der Übergabe ───────────────────────────────────

const DEBT_SYNC_NOW = gql`
  mutation DebtSyncNow {
    debtSyncNow {
      checked
      changed
      collected
      closed
      feedbackRequired
      errors
    }
  }
`;

const DEBT_REPORT_DIRECT_PAYMENT = gql`
  mutation DebtReportDirectPayment($stripeInvoiceId: String!, $amount: Float, $paymentDate: String, $note: String) {
    debtReportDirectPayment(stripeInvoiceId: $stripeInvoiceId, amount: $amount, paymentDate: $paymentDate, note: $note)
  }
`;

const DEBT_WITHDRAW = gql`
  mutation DebtWithdraw($stripeCustomerId: String!, $reason: String!, $invoiceIds: [String!], $note: String) {
    debtWithdraw(stripeCustomerId: $stripeCustomerId, reason: $reason, invoiceIds: $invoiceIds, note: $note)
  }
`;

const DEBT_ANSWER_FEEDBACK = gql`
  mutation DebtAnswerFeedback($stripeCustomerId: String!, $comment: String!, $contactEmail: String) {
    debtAnswerFeedback(stripeCustomerId: $stripeCustomerId, comment: $comment, contactEmail: $contactEmail)
  }
`;

const DEBT_WITHDRAWAL_FEES = gql`
  query DebtWithdrawalFees($stripeCustomerId: String!) {
    debtWithdrawalFees(stripeCustomerId: $stripeCustomerId) {
      cancellationFee
      successFee
    }
  }
`;

const DEBT_PAYOUTS_CSV = gql`
  query DebtPayoutsCsv($from: String!, $to: String!) {
    debtPayoutsCsv(from: $from, to: $to)
  }
`;

export type WithdrawReason =
  | 'PaidDirectlyToCreditor'
  | 'InvalidOrDisputedClaim'
  | 'CreditorWishWithoutReason'
  | 'CaseNotEconomical';

export interface DebtSyncResult {
  checked: number;
  changed: number;
  collected: number;
  closed: number;
  feedbackRequired: number;
  errors: string[];
}

export function useDebtSyncNowMutation() {
  return useMutation<{ debtSyncNow: DebtSyncResult }>(DEBT_SYNC_NOW);
}

export function useDebtReportDirectPaymentMutation() {
  return useMutation<
    { debtReportDirectPayment: boolean },
    { stripeInvoiceId: string; amount?: number | null; paymentDate?: string | null; note?: string | null }
  >(DEBT_REPORT_DIRECT_PAYMENT);
}

export function useDebtWithdrawMutation() {
  return useMutation<
    { debtWithdraw: number },
    { stripeCustomerId: string; reason: WithdrawReason; invoiceIds?: string[] | null; note?: string | null }
  >(DEBT_WITHDRAW);
}

export function useDebtAnswerFeedbackMutation() {
  return useMutation<
    { debtAnswerFeedback: boolean },
    { stripeCustomerId: string; comment: string; contactEmail?: string | null }
  >(DEBT_ANSWER_FEEDBACK);
}

export function useDebtWithdrawalFeesLazyQuery() {
  return useLazyQuery<
    { debtWithdrawalFees: { cancellationFee: number; successFee: number } },
    { stripeCustomerId: string }
  >(DEBT_WITHDRAWAL_FEES, { fetchPolicy: 'network-only' });
}

export function useDebtPayoutsCsvLazyQuery() {
  return useLazyQuery<{ debtPayoutsCsv: string }, { from: string; to: string }>(DEBT_PAYOUTS_CSV, {
    fetchPolicy: 'network-only',
  });
}
